import React, { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartOptions,
  LegendItem,
  ChartEvent
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export interface ChartSeriesPoint {
  date: string;
  starts: number;
  wins: number;
  hint1Count: number;
  hint2Count: number;
  hint3Count: number;
  medianElapsedTimeMs: number | null;
}

export const MEDIAN_SOLVE_LABEL = 'Median solve time (s)';

interface LineChartProps {
  chartData: ChartData<'line'>;
  modeLabel: string;
  points: ChartSeriesPoint[];
  statsDays?: number;
}

const SERIES_VALUE_KEYS: Record<string, keyof ChartSeriesPoint> = {
  Starts: 'starts',
  Wins: 'wins',
};

function initialHiddenIndices(chartData: ChartData<'line'>): Set<number> {
  return new Set(
    chartData.datasets
      .map((dataset, index) => (dataset.hidden ? index : -1))
      .filter((index) => index >= 0)
  );
}

function buildAccessibleLabel(
  modeLabel: string,
  points: ChartSeriesPoint[],
  visibleLabels: string[],
  statsDays: number
): string {
  if (points.length === 0) {
    return `${modeLabel} puzzle metrics chart showing no data for the last ${statsDays} days.`;
  }

  const startDate = points[0].date;
  const endDate = points[points.length - 1].date;
  const latest = points[points.length - 1];
  const latestValues = visibleLabels
    .map((label) => {
      if (label === MEDIAN_SOLVE_LABEL) {
        if (latest.medianElapsedTimeMs == null) return `${label}: —`;
        return `${label}: ${Math.round(latest.medianElapsedTimeMs / 1000)}`;
      }
      const key = SERIES_VALUE_KEYS[label];
      if (!key) return null;
      return `${label}: ${latest[key]}`;
    })
    .filter((value): value is string => value != null)
    .join(', ');

  return `${modeLabel} puzzle metrics from ${startDate} to ${endDate}. Visible series: ${visibleLabels.join(', ')}. Most recent day: ${latestValues}.`;
}

const LineChart: React.FC<LineChartProps> = ({
  chartData,
  modeLabel,
  points,
  statsDays = 30,
}) => {
  const [hiddenIndices, setHiddenIndices] = useState(() => initialHiddenIndices(chartData));

  useEffect(() => {
    setHiddenIndices(initialHiddenIndices(chartData));
  }, [chartData]);

  const visibleLabels = useMemo(
    () =>
      chartData.datasets
        .map((dataset, index) =>
          !hiddenIndices.has(index) && dataset.label ? dataset.label : null
        )
        .filter((label): label is string => label != null),
    [chartData.datasets, hiddenIndices]
  );

  const ariaLabel = useMemo(
    () => buildAccessibleLabel(modeLabel, points, visibleLabels, statsDays),
    [modeLabel, points, visibleLabels, statsDays]
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            boxWidth: 12,
            padding: 16,
          },
          onClick: (_event: ChartEvent, legendItem: LegendItem, legend) => {
            const index = legendItem.datasetIndex;
            if (index === undefined) return;

            const chart = legend.chart;
            const meta = chart.getDatasetMeta(index);
            meta.hidden =
              meta.hidden === null ? !chart.data.datasets[index].hidden : !meta.hidden;

            setHiddenIndices((prev) => {
              const next = new Set(prev);
              if (meta.hidden) {
                next.add(index);
              } else {
                next.delete(index);
              }
              return next;
            });

            chart.update();
          },
        },
        title: {
          display: false,
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1,
          },
          grid: {
            color: '#E5E7EB',
          },
          title: {
            display: true,
            text: 'Players',
          },
        },
        y1: {
          position: 'right',
          beginAtZero: true,
          grid: {
            drawOnChartArea: false,
          },
          title: {
            display: true,
            text: 'Median solve (s)',
          },
        },
        x: {
          grid: {
            color: '#E5E7EB',
          },
        },
      },
    }),
    []
  );

  return (
    <div className="line-chart-accessible" role="img" aria-label={ariaLabel}>
      <Line options={options} data={chartData} />
    </div>
  );
};

export default LineChart;
